import React, { useState, useEffect } from "react";
import { feedbackAPI } from "../services/api";
import "./Testimonials.css";

const Testimonials = ({ limit = 6, highlightedOnly = false }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({ averageRating: 0, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        const response = await feedbackAPI.getPublicFeedback({
          limit,
          highlightedOnly: highlightedOnly.toString(),
        });

        setTestimonials(response.data.feedback);
        setStats(response.data.stats);
      } catch (err) {
        console.error("Error fetching testimonials:", err);
        setError("Unable to load testimonials. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, [limit, highlightedOnly]);

  // Format the date to a more readable format
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Render stars based on rating
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(
          <span key={i} className="star full">
            ★
          </span>
        );
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(
          <span key={i} className="star half">
            ★
          </span>
        );
      } else {
        stars.push(
          <span key={i} className="star empty">
            ☆
          </span>
        );
      }
    }

    return <div className="testimonial-stars">{stars}</div>;
  };

  // Convert 10-point scale to 5-point scale for display
  const convertRating = (rating) => {
    return rating / 2;
  };

  if (loading) {
    return <div className="testimonials-loading">Loading testimonials...</div>;
  }

  if (error) {
    return <div className="testimonials-error">{error}</div>;
  }

  if (testimonials.length === 0) {
    return null; // Don't show anything if no testimonials
  }

  return (
    <section className="testimonials-section">
      <div className="testimonials-header">
        <h2>What Our Customers Say</h2>
        {stats.totalCount > 0 && (
          <div className="testimonials-stats">
            <div className="average-rating">
              <span className="rating-value">
                {convertRating(stats.averageRating).toFixed(1)}
              </span>
              {renderStars(convertRating(stats.averageRating))}
              <span className="rating-count">
                Based on {stats.totalCount} reviews
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="testimonials-grid">
        {testimonials.map((testimonial) => (
          <div key={testimonial._id} className="testimonial-card">
            <div className="testimonial-rating">
              {renderStars(convertRating(testimonial.rating))}
            </div>
            <p className="testimonial-comment">{testimonial.comment}</p>
            <div className="testimonial-footer">
              <p className="testimonial-author">
                {testimonial.user
                  ? `${testimonial.user.firstName} ${
                      testimonial.user.lastName?.charAt(0) || ""
                    }`
                  : "Anonymous"}
              </p>
              <p className="testimonial-date">
                {formatDate(testimonial.createdAt)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Testimonials;
